// Core data model for a project. Pure data — no audio nodes here.
import { uid, pickColor } from "../util";

export type EffectKind =
  | "eq" | "compressor" | "delay" | "reverb" | "gain"
  | "lowpass" | "highpass" | "saturator" | "chorus" | "limiter" | "width"
  | "denoise" | "deverb" | "wam";

export interface EffectParam {
  [key: string]: number | string | boolean;
}

export interface Effect {
  id: string;
  kind: EffectKind;
  name: string;
  enabled: boolean;
  params: EffectParam;
  /** For WAM plugins: the module descriptor URL and serialized state. */
  wamUrl?: string;
  wamState?: any;
}

/** A user-saved effect preset (built-in effects), scoped to an effect kind so it
 *  only appears in that effect's preset list. Travels with the project. */
export interface FxPreset {
  id: string;
  kind: EffectKind;
  name: string;
  params: EffectParam;
}

/** Per-clip visual transform for video compositing. Absent === identity. */
export interface ClipTransform {
  x: number;        // horizontal offset, fraction of frame width (-1..1), 0 = centered
  y: number;        // vertical offset, fraction of frame height (-1..1), 0 = centered
  scale: number;    // 1 === fit-to-frame (contain); >1 zooms in
  rotation: number; // degrees, clockwise
  opacity: number;  // 0..1
}

/** Per-clip crop: fraction of the source frame removed from each edge (0..1). */
export interface ClipCrop { t: number; r: number; b: number; l: number; }

export const IDENTITY_TRANSFORM: ClipTransform = { x: 0, y: 0, scale: 1, rotation: 0, opacity: 1 };

export interface Clip {
  id: string;
  name: string;
  assetId: string;
  start: number; // seconds on the timeline
  duration: number; // seconds (visible length)
  offset: number; // seconds into the source asset
  gain: number; // linear
  fadeIn: number; // seconds
  fadeOut: number; // seconds
  /** Takes sharing a takeGroupId occupy the same slot; only the active one plays. */
  takeGroupId?: string;
  takeActive?: boolean; // undefined === active
  /** Clips sharing a linkId move/trim/split/delete together (e.g. a video + its
   *  audio) until unlinked. */
  linkId?: string;
  /** Video-only: visual transform + crop. Absent === full-frame identity. */
  transform?: ClipTransform;
  crop?: ClipCrop;
  /** Playback speed (1 = normal). The clip covers `duration` timeline seconds and
   *  reads `duration * speed` seconds of source. Audio pitch follows speed. */
  speed?: number;
  /** Disabled clips stay on the timeline but are skipped in playback/export. */
  disabled?: boolean;
  /** Transition (seconds) from the clip that ends exactly where this one starts on
   *  the same track, centered on the cut: a cross-dissolve on video lanes, a
   *  crossfade on audio lanes. Ignored when no clip abuts this one. */
  transitionIn?: number;
}

/** A clip's playback speed (absent === 1). */
export const clipSpeed = (c: { speed?: number }): number => (c.speed && c.speed > 0 ? c.speed : 1);
/** Seconds of source media the clip reads (its timeline duration × speed). */
export const sourceSpan = (c: Clip): number => c.duration * clipSpeed(c);
/** Source-media time shown/heard at timeline position `pos` inside the clip. */
export const sourceTimeAt = (c: Clip, pos: number): number => c.offset + (pos - c.start) * clipSpeed(c);

/** A named point on the timeline (M adds one at the playhead). */
export interface Marker { id: string; time: number; name: string; }

export type TrackKind = "audio" | "instrument" | "video";

/** How a new recording interacts with existing audio on the same track. */
export type RecordMode = "comp" | "sos" | "overwrite";
export const RECORD_MODE_LABELS: Record<RecordMode, string> = {
  comp: "Comping (takes)",
  sos: "Sound on Sound",
  overwrite: "Overwrite",
};

export interface Track {
  id: string;
  name: string;
  kind: TrackKind;
  color: string;
  volume: number; // linear 0..~2
  pan: number; // -1..1
  mute: boolean;
  solo: boolean;
  armed: boolean;
  inputDeviceId: string | null;
  monitor: boolean;
  inputGain: number; // dB applied to the live input (monitor + recording)
  /** Which input channel(s) to record. Default "1" = mono from input 1 — the
   *  DAW convention for a mic (a 2-input interface's unused input 2 is hiss). */
  inputChannel?: "1" | "2" | "stereo";
  height: number; // px lane height
  compExpanded?: boolean; // show take drawer
  disabled?: boolean; // video tracks: output hidden from the composite/export
  clips: Clip[];
  fx: Effect[];
}

export const BASE_TRACK_HEIGHT = 120;
export const TAKE_ROW_HEIGHT = 28;

export type MediaKind = "audio" | "video";

export interface MediaAsset {
  id: string;
  name: string;
  kind: MediaKind;       // "audio" (default) or "video"
  sampleRate: number;    // audio sample rate (0 if the asset has no audio)
  channels: number;      // audio channel count (0 if none)
  length: number;        // audio frames (0 if none)
  duration: number;      // seconds (media duration)
  // Video-only:
  width?: number;        // intrinsic frame width (px)
  height?: number;       // intrinsic frame height (px)
  fps?: number;          // source frame rate, if known
  hasAudio?: boolean;    // true if a decoded audio buffer exists for this asset
  /** Video assets: ids of the child audio assets extracted from the file (one
   *  per audio track). They live in project.assets but are hidden from the media
   *  bin and placed alongside the video as separate linked audio lanes. */
  audioAssetIds?: string[];
  /** Child audio assets carry the owning video asset id (hidden from media bin). */
  ownerId?: string;
  /** Video assets: true while audio is still being extracted in the background. */
  audioPending?: boolean;
  /** Video assets: why audio is missing (unsupported codec, too large, …), if it is. */
  audioNote?: string;
  /** Generated media (color matte, bars & tone, title, countdown…): drawn/synthesized
   *  from this spec instead of a file, so it saves as a few bytes of JSON. */
  generator?: GeneratorSpec;
  /** Length a new clip gets when placed (generated media can be trimmed longer). */
  defaultDuration?: number;
}

export type TextAlign = "left" | "center" | "right";
/** Title / text card. Sizes and positions are fractions of the frame so the same
 *  title looks right at any sequence resolution. */
export interface TextSpec {
  type: "text";
  text: string;
  font: string;
  size: number;          // text height as a fraction of frame height (0.08 = 8%)
  color: string;
  bold: boolean;
  italic: boolean;
  align: TextAlign;
  x: number;             // anchor, 0..1 of frame width (left/center/right edge per align)
  y: number;             // vertical center of the text block, 0..1 of frame height
  outline: number;       // outline width as a fraction of the text size (0 = none)
  outlineColor: string;
  shadow: boolean;
  bg: "none" | "box" | "full";
  bgColor: string;
  bgOpacity: number;     // 0..1
}
export type GeneratorSpec =
  | { type: "color"; color: string }
  | { type: "gradient"; from: string; to: string; angle: number }
  | { type: "bars"; tone: boolean; freq: number; db: number }
  | TextSpec
  | { type: "countdown"; from: number; bg: string; accent: string; beep: boolean }
  // audio-only generators (children of bars/countdown, or standalone tone)
  | { type: "tone"; freq: number; db: number }
  | { type: "beeps"; count: number; freq: number; db: number };

/** Back-compat alias — most audio code refers to assets as AudioAsset. */
export type AudioAsset = MediaAsset;

export interface Project {
  id: string;
  name: string;
  tempo: number;
  beatsPerBar: number;
  // Video sequence settings — the composited output frame + frame rate.
  width: number;   // px (default 1920)
  height: number;  // px (default 1080)
  fps: number;     // frames per second (default 30)
  masterVolume: number;
  masterFx: Effect[];
  recordMode: RecordMode; // how new takes interact with existing audio (project-wide)
  tracks: Track[];
  assets: MediaAsset[];
  fxPresets?: FxPreset[]; // user-saved effect presets
  loopEnabled: boolean;
  loopStart: number;
  loopEnd: number;
  /** Timeline markers + the In/Out range used by Lift/Extract (I / O keys). */
  markers?: Marker[];
  markIn?: number;
  markOut?: number;
  createdAt: number;
  modifiedAt: number;
  /** Project format revision this was saved with (absent = 1, before versioning). */
  formatVersion?: number;
  /** App that wrote the file, e.g. "WebCut 0.3.0" (informational). */
  savedWith?: string;
}

/** Bump when the saved project shape changes incompatibly; add a migration in
 *  normalizeProject. Files with a higher number came from a newer WebCut. */
export const PROJECT_FORMAT_VERSION = 2;
export const APP_VERSION = "0.3.0";

export function newClip(assetId: string, asset: AudioAsset, start = 0): Clip {
  return {
    id: uid("clip"),
    name: asset.name,
    assetId,
    start,
    duration: Math.min(asset.defaultDuration ?? asset.duration, asset.duration),
    offset: 0,
    gain: 1,
    fadeIn: 0.005,
    fadeOut: 0.005,
  };
}

export const TRACK_PREFIX: Record<TrackKind, string> = { audio: "Audio", instrument: "Inst", video: "Video" };

export function newTrack(index: number, kind: TrackKind = "audio"): Track {
  return {
    id: uid("trk"),
    name: `${TRACK_PREFIX[kind]} ${index + 1}`,
    kind,
    color: pickColor(index),
    volume: 1,
    pan: 0,
    mute: false,
    solo: false,
    armed: false,
    inputDeviceId: null,
    monitor: false,
    inputGain: 0,
    height: 96,
    clips: [],
    fx: [],
  };
}

export function newProject(name = "Untitled Project"): Project {
  return {
    id: uid("proj"),
    name,
    tempo: 120,
    beatsPerBar: 4,
    width: 1920,
    height: 1080,
    fps: 30,
    masterVolume: 1,
    masterFx: [],
    recordMode: "comp",
    // A video editor opens video-first: one video lane over one audio lane.
    tracks: [newTrack(0, "video"), newTrack(0, "audio")],
    assets: [],
    loopEnabled: false,
    loopStart: 0,
    loopEnd: 0, // no region: loop plays In->Out if set, else the whole program
    createdAt: Date.now(),
    modifiedAt: Date.now(),
  };
}

/** Bring a loaded/imported project up to date. Before this version nothing in the
 *  UI set a loop region, so every project carried an untouched 0-8 s default that
 *  made the loop button repeat only the first 8 seconds: treat it as "no region". */
export function normalizeProject(p: Project): Project {
  // Every load path runs this, so files from any earlier WebCut version (and
  // hand-edited or partially damaged ones) come back with sane, complete fields.
  const num = (v: any, d: number, lo = -Infinity, hi = Infinity) => (typeof v === "number" && isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);
  const fresh = newProject();
  p.id ||= fresh.id;
  p.name = typeof p.name === "string" && p.name ? p.name : "Untitled Project";
  p.tempo = num(p.tempo, 120, 20, 400);
  p.beatsPerBar = Math.round(num(p.beatsPerBar, 4, 1, 32));
  p.width = Math.round(num(p.width, 1920, 16, 8192));
  p.height = Math.round(num(p.height, 1080, 16, 8192));
  p.fps = num(p.fps, 30, 1, 240);
  p.masterVolume = num(p.masterVolume, 1, 0, 4);
  p.masterFx = Array.isArray(p.masterFx) ? p.masterFx : [];
  p.recordMode ||= "comp";
  p.assets = Array.isArray(p.assets) ? p.assets : [];
  p.tracks = Array.isArray(p.tracks) ? p.tracks : [];
  p.loopEnabled = !!p.loopEnabled;
  p.loopStart = num(p.loopStart, 0, 0);
  p.loopEnd = num(p.loopEnd, 0, 0);
  // Before this version nothing in the UI set a loop region, so every project
  // carried an untouched 0-8 s default that made the loop button repeat only the
  // first 8 seconds: treat it as "no region".
  if (p.loopStart === 0 && p.loopEnd === 8) p.loopEnd = 0;
  if (p.markers && !Array.isArray(p.markers)) p.markers = undefined;
  p.createdAt = num(p.createdAt, Date.now());
  p.modifiedAt = num(p.modifiedAt, Date.now());

  for (const a of p.assets) {
    a.kind = a.kind === "video" ? "video" : "audio"; // v1 assets had no kind → audio
    a.name ||= "media";
    a.duration = num(a.duration, 0, 0);
  }
  p.tracks.forEach((t, i) => {
    t.id ||= `trk_${i}`;
    t.kind = t.kind === "video" || t.kind === "instrument" ? t.kind : "audio";
    t.name ||= `${TRACK_PREFIX[t.kind]} ${i + 1}`;
    t.color ||= pickColor(i);
    t.volume = num(t.volume, 1, 0, 4);
    t.pan = num(t.pan, 0, -1, 1);
    t.mute = !!t.mute; t.solo = !!t.solo; t.armed = false; // never reopen record-armed
    t.monitor = !!t.monitor;
    t.inputDeviceId ??= null;
    t.inputGain = num(t.inputGain, 0, -60, 24);
    t.height = num(t.height, 96, 20, 1000);
    t.fx = Array.isArray(t.fx) ? t.fx : [];
    t.clips = (Array.isArray(t.clips) ? t.clips : []).filter((c) => c && typeof c.assetId === "string");
    for (const c of t.clips) {
      c.id ||= `clip_${Math.random().toString(36).slice(2, 10)}`;
      c.name ??= "clip";
      c.start = num(c.start, 0, 0);
      c.duration = num(c.duration, 0, 0);
      c.offset = num(c.offset, 0, 0);
      c.gain = num(c.gain, 1, 0, 16);
      c.fadeIn = num(c.fadeIn, 0, 0);
      c.fadeOut = num(c.fadeOut, 0, 0);
      if (c.speed != null) c.speed = num(c.speed, 1, 0.1, 16);
      if (c.transitionIn != null) c.transitionIn = num(c.transitionIn, 0, 0) || undefined;
    }
    t.clips = t.clips.filter((c) => c.duration > 0); // zero-length clips are unusable
  });
  // Video tracks always sit above audio tracks.
  p.tracks.sort((a, b) => (a.kind === "video" ? 0 : 1) - (b.kind === "video" ? 0 : 1));
  p.formatVersion = PROJECT_FORMAT_VERSION;
  return p;
}
